import {
  MutationCache,
  QueryCache,
  QueryClient,
  type QueryClientConfig,
  type UseQueryOptions,
} from '@tanstack/react-query';
import _ from 'lodash';
import { registerQueryClient } from './query-client-registry';
import { createQueryErrorHandler, type QueryErrorReporter } from './query-errors';

/**
 * Per-hook query option overrides — everything a caller may tweak except the
 * hook-owned `queryKey`/`queryFn`. Shared so hook files don't each redeclare it.
 */
export type QueryOpts<T> = Partial<Omit<UseQueryOptions<T>, 'queryKey' | 'queryFn'>>;

// `staleTime` of 5 min: data fetched within this window is reused without a
// refetch, so in-session navigations are cache hits. The default is 0, which
// would refetch on every mount.
// `refetchOnWindowFocus` is off because mutations invalidate explicitly; focus
// refetches were noisy in practice.
const defaultOptions: QueryClientConfig['defaultOptions'] = {
  queries: {
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  },
};

export interface QueryClientOptions {
  /**
   * Called once per unexpected query/mutation failure, after retries, with a
   * safe summary and the raw error. Omit for no reporting. See
   * `createQueryErrorHandler` for which failures are filtered out first.
   */
  onError?: QueryErrorReporter;
}

/**
 * The caches are the only global error hooks in react-query 5. `useQuery` has
 * no `onError` (removed in v5) and `defaultOptions.mutations.onError` is
 * replaced by any per-hook `onError` (shallow spread in
 * `defaultMutationOptions`), so neither is a reliable global path.
 * `QueryCache`/`MutationCache.onError` always fire, once per fetch, after
 * retries — the path the maintainers recommend for global handling.
 */
const reportingCaches = (
  onError: QueryErrorReporter | undefined,
): Pick<QueryClientConfig, 'queryCache' | 'mutationCache'> => {
  if (!onError) return {};
  const report = createQueryErrorHandler(onError);
  return {
    queryCache: new QueryCache({
      onError: (error, query) => report('query', query.queryKey, error),
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) =>
        report('mutation', mutation.options.mutationKey, error),
    }),
  };
};

/**
 * Each host app (web, mobile, back-office) needs its own `QueryClient`
 * instance — caches are scoped to a React root — but they should share the
 * same defaults. Pass `overrides` to tweak per-app, and `onError` to report
 * failed queries and mutations to that host's monitoring sink.
 *
 * Registers the created client in the shared registry as a side effect, so
 * "created" == "registered" and no host can forget the step (which would make
 * thunk-side `invalidateQueryCache` a silent no-op). One client per host, so
 * last-created-wins is the intended semantics; a test re-creating a client
 * simply re-registers it.
 *
 * `queryCache`/`mutationCache` are not overridable: replacing them would
 * silently disable `onError` reporting.
 */
export const createQueryClient = (
  overrides: Omit<QueryClientConfig, 'queryCache' | 'mutationCache'> = {},
  { onError }: QueryClientOptions = {},
): QueryClient => {
  // Empty destination: lodash writes into nested plain-object destinations, so
  // merging into `{ defaultOptions }` would mutate the module-level constant
  // and leak one client's overrides into the next.
  const queryClient = new QueryClient(
    _.merge({}, { defaultOptions }, reportingCaches(onError), overrides),
  );
  registerQueryClient(queryClient);
  return queryClient;
};
